import { fromPromise } from "xstate";
import type { DeletionEvent } from "../state-machines/deletion-machine";
import type { PowerSyncDrizzleDB } from "@listto/powersync";
import { createListMutation, updateListMutation } from "./mutations";

export interface ListActions {
  createList: (
    name: string,
    opts?: {
      color?: string;
      icon?: string;
      groupId?: string;
      themeId?: string | null;
      isBlur?: boolean;
    },
  ) => Promise<string | undefined>;
  updateList: (
    id: string,
    updates: Partial<{
      name: string;
      color: string | null;
      icon: string | null;
      theme_id: string | null;
      is_blur: number;
    }>,
  ) => Promise<void>;
  createGroup: (name: string) => Promise<string | undefined>;
  updateGroup: (id: string, updates: Partial<{ name: string }>) => Promise<void>;
  assignListToGroup: (listId: string, groupId: string | null) => Promise<void>;
}

export function createListHandlers(
  sendDeletion: (event: DeletionEvent) => void,
  actions: ListActions,
) {
  return {
    handleCreateList: async (name: string, opts?: Parameters<ListActions["createList"]>[1]) =>
      actions.createList(name, opts),
    handleUpdateList: async (id: string, updates: Parameters<ListActions["updateList"]>[1]) =>
      actions.updateList(id, updates),
    handleDeleteList: (id: string) => {
      sendDeletion({ type: "DELETE", itemId: id, itemType: "list" });
    },
    handleCreateGroup: async (name: string) => actions.createGroup(name),
    handleUpdateGroup: async (id: string, updates: Parameters<ListActions["updateGroup"]>[1]) =>
      actions.updateGroup(id, updates),
    handleDeleteGroup: (id: string) => {
      sendDeletion({ type: "DELETE", itemId: id, itemType: "group" });
    },
    handleAssignListToGroup: async (listId: string, groupId: string | null) =>
      actions.assignListToGroup(listId, groupId),
  };
}

/**
 * Provides standard actor implementations for listFormMachine.
 * Unifies logic between native and web apps.
 */
/**
 * CC-8: `db` and `userId` are ACCESSORS, read lazily when an actor RUNS, not
 * snapshotted when the actors are provided. The list dialog is mounted at app
 * boot (its `open` prop is just toggled), so at provide-time the PowerSync
 * drizzle db is often still null and the visitor's `userId` not yet resolved.
 * Freezing those values (as the old value-based signature did) made "create
 * list" throw "Database not initialized" / "User not authenticated" forever —
 * the save machine never reached `success` and the dialog stayed open. Reading
 * them at save time uses whatever is current when the user actually submits.
 */
export function getListFormActors(options: {
  db: () => PowerSyncDrizzleDB | null;
  userId: () => string | undefined;
}) {
  const getDb = options.db;
  const getUserId = options.userId;

  return {
    saveListActor: fromPromise<
      // oxlint-disable-next-line typescript-eslint/no-invalid-void-type
      void,
      {
        id?: string;
        groupId?: string;
        values: {
          name: string;
          color: string;
          icon: string;
          themeId: string | null;
          isBlur: boolean;
        };
      }
    >(async ({ input }) => {
      const db = getDb();
      if (!db) {
        throw new Error("Database not initialized");
      }

      const { id, groupId, values } = input;

      if (id) {
        await updateListMutation(db, id, {
          name: values.name.trim(),
          color: values.color,
          icon: values.icon,
          theme_id: values.themeId,
          is_blur: values.isBlur ? 1 : 0,
        });
      } else {
        const userId = getUserId();
        if (!userId) {
          throw new Error("User not authenticated");
        }
        await createListMutation(db, userId, values.name.trim(), {
          color: values.color,
          icon: values.icon,
          groupId,
          themeId: values.themeId,
          isBlur: values.isBlur,
        });
      }
    }),

    // oxlint-disable-next-line typescript-eslint/no-invalid-void-type
    updateThemePreviewActor: fromPromise<void, { id: string; themeId: string | null }>(
      async ({ input }) => {
        const db = getDb();
        if (!db || !input.id) {
          return;
        }
        await updateListMutation(db, input.id, { theme_id: input.themeId });
      },
    ),
  };
}
